import React, { useEffect, useState, useRef } from "react";
import { Storage, API, graphqlOperation } from "aws-amplify";
import { Button, Loader } from "@aws-amplify/ui-react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { lotPhotosByLotID, cropUnitsByLotID, getLot } from "graphql/queries";
import {
  createLotPhoto,
  updateLotPhoto,
} from "graphql/mutations";

import LotDemarcationCanvas from "components/agro/lots/LotDemarcationCanvas";

const LotPhotoDemarcation = ({ lotId }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [existingPhotoId, setExistingPhotoId] = useState(null);
  const [cropUnitsForCanvas, setCropUnitsForCanvas] = useState([]);

  // Cache for lot info needed for mutations
  const [lotInfo, setLotInfo] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!lotId) {
      setImageUrl(null);
      setExistingPhotoId(null);
      setCropUnitsForCanvas([]);
      setLotInfo(null);
      return;
    }

    const fetchAllData = async () => {
      setLoadingFetch(true);
      try {
        await Promise.all([loadExisting(), loadCropUnitsData(), loadLotInfo()]);
      } catch (error) {
        console.error("Error loading initial data", error);
        toast.error("Failed to load lot data.");
      } finally {
        setLoadingFetch(false);
      }
    };

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId]);

  const loadLotInfo = async () => {
    try {
      const res = await API.graphql(graphqlOperation(getLot, { id: lotId }));
      const lot = res?.data?.getLot;
      if (lot) {
        setLotInfo({ companyID: lot.companyID, farmID: lot.farmID });
      }
    } catch (e) {
      console.error("Error loading lot info", e);
    }
  }

  const loadCropUnitsData = async () => {
    if (!lotId) return;
    try {
      const res = await API.graphql(
        graphqlOperation(cropUnitsByLotID, {
          lotID: lotId,
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const items = res?.data?.cropUnitsByLotID?.items ?? [];
      setCropUnitsForCanvas(items);
    } catch (err) {
      console.error("Error loading crop units data", err);
      toast.error("Failed to load microlotes.");
    }
  };

  async function loadExisting() {
    if (!lotId) return;
    try {
      const res = await API.graphql(
        graphqlOperation(lotPhotosByLotID, { lotID: lotId })
      );
      const items = res?.data?.lotPhotosByLotID?.items ?? [];
      if (items.length) {
        const photo = items[0];
        setExistingPhotoId(photo.id);
        const keyOrUrl = photo.key ?? photo.url ?? "";
        if (!keyOrUrl) {
          setImageUrl(null);
          return;
        }
        if (keyOrUrl.startsWith("http")) {
          setImageUrl(keyOrUrl);
        } else {
          try {
            const resolved = await Storage.get(keyOrUrl, { level: "public" });
            setImageUrl(resolved);
          } catch (e) {
            console.warn(
              "Could not resolve stored photo key",
              e
            );
            setImageUrl(keyOrUrl);
          }
        }
      } else {
        setImageUrl(null);
        setExistingPhotoId(null);
      }
    } catch (err) {
      console.error("Error loading existing LotPhoto", err);
    }
  }

  const handleFileSelect = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file || !lotId) return;
    setUploading(true);
    try {
      const basename = file.name.split(".").slice(0, -1).join(".") || "photo";
      const ext = (file.name.split(".").slice(-1)[0] || "jpg").replace(
        /[^a-zA-Z0-9]/g,
        ""
      );
      const fileSrc = `${basename}_${Date.now()}.${ext}`;
      const key = `lot-photos/${fileSrc}`;
      await Storage.put(key, file, { contentType: file.type, level: "public" });
      const publicUrl = await Storage.get(key, { level: "public" });

      if (existingPhotoId) {
        await API.graphql(
          graphqlOperation(updateLotPhoto, {
            input: { id: existingPhotoId, lotID: lotId, key },
          })
        );
      } else {
        const resp = await API.graphql(
          graphqlOperation(createLotPhoto, {
            input: { lotID: lotId, key },
          })
        );
        const created = resp?.data?.createLotPhoto;
        if (created?.id) setExistingPhotoId(created.id);
      }

      setImageUrl(publicUrl);
      toast.success(strings.successUploaded);
    } catch (err) {
      console.error("Error uploading photo", err);
      toast.error(strings.responses.errorUpload);
    } finally {
      setUploading(false);
      try {
        event.target.value = null;
      } catch (e) {
        /* ignore */
      }
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        <Button
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={uploading}
          variation="primary"
        >
          {uploading ? (
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Loader /> {strings.upload.uploading}
            </span>
          ) : existingPhotoId ? (
            strings.upload.update
          ) : (
            strings.upload.add
          )}
        </Button>
      </div>

      <LotDemarcationCanvas
        imageUrl={imageUrl}
        cropUnits={cropUnitsForCanvas}
        readOnly={false}
        lotId={lotId}
        companyID={lotInfo?.companyID}
        farmID={lotInfo?.farmID}
        onCropUnitChange={loadCropUnitsData} // Refresh list on change
        isLoadingData={loadingFetch || uploading}
      />
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    upload: {
      add: "Subir foto",
      update: "Actualizar foto",
      uploading: "Subiendo...",
    },
    successUploaded: "Foto subida",
    responses: {
      errorUpload: "Error al subir la foto",
    },
  },
});

export default LotPhotoDemarcation;
